import fire from '@store/firebase';
import firebase from 'firebase/compat/app';
import {
  doc,
  getFirestore,
  runTransaction,
} from 'firebase/firestore';
import { appendEditHistory } from '@store/firebase/utils/editHistory';

// Firestore 서버 타임스탬프 (클라이언트 시계 오차로 인한 정렬 뒤틀림 방지)
const serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp();

import { ItimelineItem } from '@typings/pages/timeline.interface';
import { deleteImgStorage, saveAndGetImgURL } from '@store/firebase/storage';
import { updateHashtagsAndGetIdList } from '@store/firebase/hashtag/update-hashtag';
import { mentionUserNotificaton } from '../notification/update-notification';
import { processContentWithBots } from '@utils/customBot.util';
import { emitTimelineForceRefresh } from '@utils/realtimeFallbackEvent';

const firestore = fire.firestore();

export async function addAndGetDocId({ ref, data }) {
  try {
    const res = await ref.add({
      ...data,
      createdAt: serverTimestamp(),
      editedAt: serverTimestamp(),
      hashtagIdList: [],
    });
    return res.id;
  } catch (err) {
    // 업로드 실패 원인 추적: 에러를 삼키지 말고 표면화
    console.error('[addAndGetDocId] failed:', err);
    throw err;
  }
}

type Props = {
  data: ItimelineItem;
  collectionPath: string;
  docId?: string;
  hashtag: any;
  prevHashtag: any;
  fileList: {
    selectedFileList: File[] | null;
    prevImgURLList: string[];
    removeImgURLList: string[] | null;
  };
  editedBy?: string;
};

export async function updateTimeline({
  data,
  collectionPath,
  docId,
  hashtag,
  prevHashtag,
  fileList,
  editedBy,
}: Props) {
  const ref = firestore.collection(collectionPath);
  const { selectedFileList, prevImgURLList, removeImgURLList } = fileList;

  let imgURLList: string[] = prevImgURLList || [];
  // 여러 이미지를 뽑아와야함.
  if (selectedFileList) {
    const newImgURLList = await Promise.all(
      selectedFileList.map(async (selectedFile: File, i) => {
        // 기존이미지는 삭제하고, 신규이미지는 추가
        if (selectedFile) {
          try {
            const resURL = await saveAndGetImgURL(collectionPath, selectedFile);
            return resURL;
          } catch (err) {
            alert(err.message);
          }
        }
      }),
    );

    const validNewImgURLList = newImgURLList.filter(
      (url): url is string => Boolean(url),
    );
    imgURLList = [...prevImgURLList, ...validNewImgURLList];
  }

  // 삭제된 이미지를 저장소에서도 삭제

  removeImgURLList?.map((url) => {
    if (url) deleteImgStorage(url);
  });

  let _docId: string = docId || '';
  // numberId는 신규 작성 시에만 계산 (수정 시에는 기존 값을 그대로 유지)
  let numberId: number | undefined;

  if (!docId) {
    // _meta 컬렉션에서 lastNumberId를 트랜잭션으로 increment하여
    // 동시 업로드 시에도 유일한 numberId를 보장한다.
    // meta-collection-separation 마이그레이션 완료 — legacy 경로({collectionPath}/_meta)는 참조하지 않음.
    const db = getFirestore();
    const metaRef = doc(db, '_meta', collectionPath);

    numberId = await runTransaction(db, async (transaction) => {
      const metaSnap = await transaction.get(metaRef);
      const last = metaSnap.exists() ? (metaSnap.data()?.lastNumberId ?? 0) : 0;
      const next = (last as number) + 1;
      transaction.set(metaRef, { lastNumberId: next }, { merge: true });
      return next;
    });

    //추가 (numberId를 포함하여 onCreate 트리거에서도 올바른 값이 Algolia에 저장되도록 함)
    _docId = await addAndGetDocId({
      ref,
      data: {
        ...data,
        imgURLList,
        ...(numberId !== undefined && { numberId }),
      },
    });
  }

  // hashtag 추가 및 재설정
  const hashtagIdList = await updateHashtagsAndGetIdList({
    hashtagList: hashtag,
    collectionPath: collectionPath,
    prevHashIdList: prevHashtag,
    docId: _docId,
  });

  // 🎯 커스텀 봇 실행 (신규 작성만, 수정 시에는 실행 안 함)
  const isUpdate = !!docId; // docId가 있으면 수정 모드
  const { contents: processedContents, botResults } = await processContentWithBots(
    data.contents || '',
    isUpdate
  );

  if (botResults) {
    console.log('🤖 타임라인 봇 실행 결과:', botResults);
  }

  // 수정 이력: Firestore에서 누적된 editHistory를 읽고, 새 내용(스냅샷)을 히스토리에 저장
  let editHistoryPatch: Partial<ItimelineItem> = {};
  if (docId && editedBy) {
    const currentDoc = await ref.doc(docId).get();
    const currentData = currentDoc.data() as ItimelineItem | undefined;
    editHistoryPatch = {
      lastEditedBy: editedBy,
      editHistory: appendEditHistory(
        currentData?.editHistory,
        editedBy,
        undefined,
        data.contents, // 새로 저장하는 내용(스냅샷)
      ),
    };
  }

  const newData: ItimelineItem = {
    ...data,
    contents: processedContents, // 🎯 원본 contents 그대로
    imgURLList,
    imgURL: '', //imgURL 비우기 (list[]로 대체)
    hashtagIdList,
    editedAt: serverTimestamp() as any,
    ...editHistoryPatch,
    // 신규 작성 시에만 numberId 설정 (수정 시에는 필드를 포함하지 않아 기존 값 보존)
    ...(numberId !== undefined && { numberId }),
    // 🎯 봇 결과 저장 (신규 작성 시에만)
    ...(botResults ? { botResults } : {}),
  };

  // 알림 추가 -------------------------------
  const notificationData = {
    contents: data.contents,
    sender: data.authorUid!,
    createdAt: new Date(),
    collectionPath,
    is_seen: false,
    postId: _docId,
  };
  mentionUserNotificaton({ data: notificationData });
  // 알림 추가 end -------------------------------

  await ref
    .doc(_docId)
    .set(newData, { merge: true })
    .then(() => {
      // 낙관적 UI 패치는 FieldValue sentinel 대신 클라이언트 Date로 대체
      // (서버 reconcile 시 실제 serverTimestamp 값으로 덮어써짐)
      const optimisticNow = new Date();
      const patchForLocal = {
        ...newData,
        editedAt: optimisticNow,
        ...(docId ? {} : { createdAt: optimisticNow }),
      } as any;
      emitTimelineForceRefresh({
        collectionPath,
        action: docId ? 'update' : 'create',
        docId: _docId,
        patch: patchForLocal,
        ...(docId ? {} : { reconcileAfterMs: 2000 }),
      });
    })
    .catch((err) => console.log(err.message));

  return _docId;
}
